import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { useCustomerLocations } from '@/hooks/useCustomers';
import {
  useCreateLocation,
  useDeleteLocation,
  useUpdateLocation,
} from '@/hooks/useLocations';
import { formatDateTime } from '@/lib/format';
import type { Location, LocationCreate } from '@/types/api';

/**
 * Standort-Verwaltung auf der Kunden-Detailseite (Spec S8-06 §5).
 * Liste + Anlegen/Bearbeiten/Loeschen (soft) über Kontrakt 1.
 */

interface LocationFormState {
  name: string;
  address: string;
  notes: string;
}

function emptyLocationForm(): LocationFormState {
  return { name: '', address: '', notes: '' };
}

function formFromLocation(location: Location): LocationFormState {
  return {
    name: location.name,
    address: location.address ?? '',
    notes: location.notes ?? '',
  };
}

type LocationDialogState = { mode: 'create' } | { mode: 'edit'; location: Location };

interface Props {
  customerId: string;
}

export function LocationsCard({ customerId }: Props): JSX.Element {
  const { data, isLoading, isError, error } = useCustomerLocations(customerId);
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();
  const deleteMutation = useDeleteLocation();

  const [dialog, setDialog] = useState<LocationDialogState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Location | null>(null);

  const openDialog = useCallback((next: LocationDialogState): void => {
    createMutation.reset();
    updateMutation.reset();
    setDialog(next);
  }, [createMutation, updateMutation]);

  const submitForm = (form: LocationFormState): void => {
    const body: LocationCreate = {
      name: form.name.trim(),
      address: form.address.trim() || null,
      notes: form.notes.trim() || null,
    };
    const onSuccess = (): void => setDialog(null);
    if (dialog?.mode === 'edit') {
      updateMutation.mutate({ locationId: dialog.location.id, body }, { onSuccess });
    } else {
      createMutation.mutate({ customerId, body }, { onSuccess });
    }
  };

  const runDelete = (): void => {
    if (!confirmDelete) return;
    deleteMutation.mutate(
      { locationId: confirmDelete.id },
      { onSuccess: () => setConfirmDelete(null) }
    );
  };

  const locations = data?.data ?? [];

  const columns = useMemo((): ReadonlyArray<Column<Location>> => {
    return [
      {
        id: 'name',
        header: 'Name',
        className: 'font-medium',
        cell: (loc) => loc.name,
      },
      {
        id: 'adresse',
        header: 'Adresse',
        className: 'text-muted',
        filterValue: (loc) => loc.address ?? '',
        cell: (loc) => loc.address ?? '–',
      },
      {
        id: 'notizen',
        header: 'Notizen',
        className: 'whitespace-pre-wrap text-muted',
        filterValue: (loc) => loc.notes ?? '',
        cell: (loc) => loc.notes ?? '–',
      },
      {
        id: 'aktualisiert',
        header: 'Aktualisiert',
        className: 'text-muted',
        filterValue: (loc) => {
          const text = formatDateTime(loc.updated_at);
          return text === '–' ? '' : text;
        },
        cell: (loc) => formatDateTime(loc.updated_at),
      },
      {
        id: 'aktionen',
        header: 'Aktionen',
        filter: false,
        headerClassName: 'text-right',
        className: 'text-right',
        cell: (loc) => (
          <span className="inline-flex flex-wrap justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Pencil className="size-3" />}
              onClick={() => openDialog({ mode: 'edit', location: loc })}
            >
              Bearbeiten
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-danger"
              leftIcon={<Trash2 className="size-3" />}
              onClick={() => {
                deleteMutation.reset();
                setConfirmDelete(loc);
              }}
            >
              Löschen
            </Button>
          </span>
        ),
      },
    ];
  }, [deleteMutation, openDialog]);

  return (
    <Card
      title="Standorte"
      description="Multi-Site-Kunden bekommen pro Standort einen eigenen Eintrag."
      actions={
        <Button
          size="sm"
          leftIcon={<Plus className="size-4" />}
          onClick={() => openDialog({ mode: 'create' })}
          data-testid="location-create"
        >
          Standort anlegen
        </Button>
      }
    >
      {isLoading && <SkeletonRows rows={3} cols={4} />}
      {isError && <ErrorAlert error={error} />}

      {!isLoading && !isError && locations.length === 0 && (
        <EmptyState
          icon={<MapPin className="size-8" />}
          title="Keine Standorte erfasst"
          description="z.B. RPBW: Krems, Duernstein Bad, Spitz — jeder Standort einzeln."
          actions={
            <Button
              leftIcon={<Plus className="size-4" />}
              onClick={() => openDialog({ mode: 'create' })}
            >
              Standort anlegen
            </Button>
          }
        />
      )}

      {locations.length > 0 && (
        <DataTable
          caption="Standorte des Kunden"
          columns={columns}
          rows={locations}
          rowKey={(loc) => loc.id}
          storageKey="kunde-standorte"
          rowTestId={(loc) => `location-row-${loc.id}`}
        />
      )}

      <LocationFormDialog
        state={dialog}
        onClose={() => setDialog(null)}
        onSubmit={submitForm}
        saving={createMutation.isPending || updateMutation.isPending}
        error={dialog?.mode === 'edit' ? updateMutation.error : createMutation.error}
      />

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Standort löschen?"
        {...(confirmDelete ? { description: confirmDelete.name } : {})}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(null)}
              disabled={deleteMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={runDelete}
              loading={deleteMutation.isPending}
              data-testid="location-confirm-delete"
            >
              Löschen
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2 text-sm">
          <p>
            Soft-Delete: der Standort wird deaktiviert; zugeordnete Systeme bleiben
            bestehen (Standort-Zuordnung wird geloest).
          </p>
          {deleteMutation.isError && <ErrorAlert error={deleteMutation.error} />}
        </div>
      </Dialog>
    </Card>
  );
}

interface LocationFormDialogProps {
  state: LocationDialogState | null;
  onClose: () => void;
  onSubmit: (form: LocationFormState) => void;
  saving: boolean;
  error: Error | null;
}

function LocationFormDialog({
  state,
  onClose,
  onSubmit,
  saving,
  error,
}: LocationFormDialogProps): JSX.Element {
  const [form, setForm] = useState<LocationFormState>(emptyLocationForm());

  // Formular beim Öffnen mit Create-Defaults bzw. Edit-Werten befüllen.
  useEffect(() => {
    if (!state) return;
    setForm(state.mode === 'edit' ? formFromLocation(state.location) : emptyLocationForm());
  }, [state]);

  const nameMissing = form.name.trim() === '';

  return (
    <Dialog
      open={Boolean(state)}
      onClose={onClose}
      title={state?.mode === 'edit' ? 'Standort bearbeiten' : 'Standort anlegen'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => onSubmit(form)}
            loading={saving}
            disabled={nameMissing}
            data-testid="location-save"
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="z.B. Krems"
          data-testid="location-name"
        />
        <Input
          label="Adresse"
          value={form.address}
          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
          placeholder="Straße, PLZ Ort"
        />
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Notizen</span>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </label>
        {error && <ErrorAlert error={error} />}
      </div>
    </Dialog>
  );
}
